/** Legal-entity suffixes and generic words that should not influence duplicate matching. */
const NOISE_WORDS = [
  'בע"מ', "בע'מ", 'בעמ', 'בע״מ', 'ע"מ', 'ע״מ', 'ltd', 'limited', 'inc', 'llc', 'בעמ.',
];

/**
 * Normalize a business name for duplicate detection:
 * lowercase, unify Hebrew geresh/gershayim, strip legal suffixes and punctuation, collapse spaces.
 * "קפה  גן-סיפור בע״מ" → "קפה גן סיפור"
 */
export function normalizeBusinessName(input: string | null | undefined): string {
  if (!input) return '';
  let s = String(input).normalize('NFKC').toLowerCase().trim();
  s = s.replace(/[‎‏‪-‮]/g, '');
  s = s.replace(/[׳'`´]/g, "'").replace(/[״"“”]/g, '"');
  for (const w of NOISE_WORDS) {
    s = s.split(w.toLowerCase()).join(' ');
  }
  s = s.replace(/[^\p{L}\p{N}\s]/gu, ' ');
  return s.replace(/\s+/g, ' ').trim();
}

export function normalizeEmail(input: string | null | undefined): string | null {
  if (!input) return null;
  const s = String(input).trim().toLowerCase();
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s) ? s : null;
}

/** First name for templates: first token of the full name. */
export function firstNameOf(fullName: string | null | undefined): string {
  if (!fullName) return '';
  return String(fullName).trim().split(/\s+/)[0] ?? '';
}

/** Render a {{variable}} template. Unknown variables are left empty. Values are plain text. */
export function renderTemplate(body: string, vars: Record<string, string | number | null | undefined>): string {
  return body.replace(/\{\{\s*([a-z_][a-z0-9_]*)\s*\}\}/gi, (_, k: string) => {
    const v = vars[k];
    return v === null || v === undefined ? '' : String(v);
  });
}

/** Simple, dependency-free trigram similarity (0..1), matching PostgreSQL pg_trgm semantics closely. */
export function trigramSimilarity(a: string, b: string): number {
  const grams = (s: string) => {
    const set = new Set<string>();
    for (const word of s.split(/\s+/).filter(Boolean)) {
      const padded = `  ${word} `;
      for (let i = 0; i < padded.length - 2; i++) set.add(padded.slice(i, i + 3));
    }
    return set;
  };
  const ga = grams(a);
  const gb = grams(b);
  if (ga.size === 0 || gb.size === 0) return 0;
  let common = 0;
  for (const g of ga) if (gb.has(g)) common++;
  return common / (ga.size + gb.size - common);
}
